import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AppraisalProgressFormHeader from "./component/AppraisalProgressFormHeader/AppraisalProgressFormHeader";
import { Tabs, Tab, Row, Col, Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { AppraisalCommentTab, DevelopmentPlanTab, SignOffTab, TabsContent, SectionCategoryTab } from "./component";
import { ApprovalModal } from "../component";
import { fetchAppraisals, createOrUpdateAppraisalProgress, getAppraisalProgressById, fetchAllEmployees, getAppraisalById, sendAppraisalProgressForRTM, operationsManagerApprove } from "actions";
import { getAppraisalPermissions, getUserRole, getCurrentUserId, getCurrentEmployeeId } from "utils/appraisalPermissions";
import { map, find } from "lodash";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useObjectViewedId } from "hooks/useObjectViewedId";
import { getObjectViewedIdFromRoute } from "utils/menuUtils";
import { F, getAppraisalSetupSectionPivotRows } from "pages/AppraisalManagment/constants/legacyApiKeys";
import {
  calculateAppraisalSummaryPercentage,
  formatSummaryPercentageDisplay,
  isValidMaxRating,
  mergeScoringRowWithSectionData,
} from "pages/AppraisalManagment/utils/appraisalProgressSummary";
import {
  getManagerRejectionRemarksFromRecord,
  isAppraisalReturnedToSupervisor,
} from "pages/AppraisalManagment/utils/appraisalProgressRecordHelpers";
import "../AppraisalProgress.css";

const resolvePersonDisplayName = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) {
    for (const item of value) {
      const candidate = resolvePersonDisplayName(item);
      if (candidate) return candidate;
    }
    return "";
  }
  if (typeof value === "object") {
    const nameParts = [value.firstname, value.middlename, value.lastname]
      .map((part) => (part == null ? "" : String(part).trim()))
      .filter(Boolean);
    if (nameParts.length > 0) return nameParts.join(" ");
    return String(
      value.full_name ||
        value.name ||
        value.employee_name ||
        value.supervisor_name ||
        value.label ||
        ""
    ).trim();
  }
  return "";
};

const mergeAppraisalProgressRecord = (record, list = []) => {
  if (!record) return record;
  const listRow = (list || []).find((item) => item?.id === record?.id) || null;
  if (!listRow) return record;

  const listEmployee = listRow.employee || {};
  const recordEmployee = record.employee || {};

  return {
    ...listRow,
    ...record,
    employee: {
      ...listEmployee,
      ...recordEmployee,
      supervisor:
        recordEmployee.supervisor ||
        listEmployee.supervisor ||
        null,
      appraisal:
        recordEmployee.appraisal ||
        listEmployee.appraisal ||
        null,
      appraisal_search_text:
        recordEmployee.appraisal_search_text ||
        listEmployee.appraisal_search_text ||
        "",
    },
    latest_reporting_to:
      record.latest_reporting_to ||
      listRow.latest_reporting_to ||
      listEmployee.latest_reporting_to ||
      recordEmployee.latest_reporting_to ||
      null,
    appraiser: record.appraiser || listRow.appraiser || null,
    appraiser_id: record.appraiser_id || listRow.appraiser_id || null,
  };
};

const resolveAppraiserNameFromRecord = (record, employeesList = []) => {
  const fromAppraiser = resolvePersonDisplayName(record?.appraiser);
  if (fromAppraiser) return fromAppraiser;

  const appraiserId = record?.appraiser_id || record?.appraiser?.id;

  if (!appraiserId || !Array.isArray(employeesList) || employeesList.length === 0) {
    return "";
  }

  const appraiserEmployee = employeesList.find(
    (emp) => String(emp?.id) === String(appraiserId)
  );
  return resolvePersonDisplayName(appraiserEmployee);
};

const resolveAppraisalFormNameFromRecord = (record) => {
  const employee = record?.employee;
  const appraisalTitle = employee?.appraisal?.[0]?.title;
  if (appraisalTitle) return String(appraisalTitle).trim();
  if (employee?.appraisal_search_text) {
    return String(employee.appraisal_search_text).trim();
  }
  if (Array.isArray(employee?.appraisal) && employee.appraisal.length > 0) {
    const titles = employee.appraisal
      .map((item) => item?.title)
      .filter(Boolean);
    if (titles.length > 0) return titles.join(", ");
  }
  return (
    record?.appraisal_name ||
    record?.setup_name ||
    record?.appraisal_setups?.[0]?.title ||
    ""
  );
};

const AppraisalProgressForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const appraisalState = useSelector((state) => state.appraisalState);
  const employeeState = useSelector((state) => state.employeeState);
  const session = useSelector((state) => state.session);
  const routeObjectViewedId = useObjectViewedId();

  const object_viewed_id =
    location.state?.objectViewedId ||
    routeObjectViewedId ||
    (session?.current_page_permissions && !Array.isArray(session.current_page_permissions)
      ? session.current_page_permissions.object_id
      : null) ||
    session?.current_object_viewed_id ||
    (session?.nested_menus
      ? getObjectViewedIdFromRoute(
          session.nested_menus,
          location.state?.from || location.pathname,
          location.state?.from || null
        )
      : null) ||
    "";

  const [selectedSectionTabs, setSelectedSectionTabs] = useState([]); // Tabs by section title
  const [activeKey, setActiveKey] = useState("");
  const [selectedAppraisals, setSelectedAppraisals] = useState([]);
  const [appraisalOptions, setAppraisalOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const routeRecordId = params?.id || null;
  const [isEditMode, setIsEditMode] = useState(location.state?.editMode || !!routeRecordId);
  const [sectionCategories, setSectionCategories] = useState({}); // Grouped by section title
  const [appraisalSetups, setAppraisalSetups] = useState([]); // Store fetched appraisal setups
  const [approvalModal, setApprovalModal] = useState({ show: false, actionType: null });
  const listPath =
    (location.state?.from && !String(location.state.from).includes("appraisalprogressform")
      ? location.state.from
      : null) ||
    (location.pathname.startsWith("/probational-appraisals")
      ? "/probational-appraisals"
      : "/appraisal");
  
  // Initialize appraisalDateTime with current date/time for new records
  const getInitialAppraisalDateTime = () => {
    const editMode = location.state?.editMode || !!routeRecordId;
    return editMode ? null : new Date();
  };
  
  // Form state
  const [formState, setFormState] = useState({
    id: null,
    employeeName: "",
    employeeId: null,
    appraisalDateTime: getInitialAppraisalDateTime(), // Set current date/time for new records
    appraisalTypes: [],
    appraisal_types: "", // String field for appraisal type like "General"
    appraisal_name: "", // Appraisal setup title/name
    appraiserName: "",
    appraisersComments: "",
    objective1: "",
    objective2: "",
    objective3: "",
    developmentalPlan: "",
    signOff: "",
    signOffDateTime: null,
    signatureImage: "",
    employeeSignature: "",
    employeeSignedAt: null,
    show_rtm: 0,
    show_rtm_and_update_button: 0,
    status: "",
    current_approval_level: null,
    approval_level: null,
    created_by: null,
    can_edit: null,
    can_approve: null,
    can_reject: null,
    approval_levels: null,
    appraiser_id: null,
    second_approver_id: null,
    sectionData: {}, // Mirrors sections_data and legacy synonym blob sent by the API
    // Add more fields as needed for other tabs
  });

  // Ensure current date/time is set for new records if somehow not set
  useEffect(() => {
    if (!isEditMode && (!formState.appraisalDateTime || formState.appraisalDateTime === null)) {
      setFormState(prev => ({
        ...prev,
        appraisalDateTime: new Date()
      }));
    }
  }, [isEditMode]); // Run when isEditMode is determined

  // Fetch employees for supervisor lookup (new + edit)
  useEffect(() => {
    if (!employeeState.employeesList?.length) {
      dispatch(fetchAllEmployees());
    }
  }, [dispatch, employeeState.employeesList?.length]);

  // Fetch appraisals on component mount (only for new records, not edit mode)
  useEffect(() => {
    // Skip fetching all appraisals in edit mode - we only need the specific progress record
    if (isEditMode) {
      return;
    }

    if (object_viewed_id) {
      dispatch(fetchAppraisals(object_viewed_id));
    } else {
      console.warn('object_viewed_id not available for fetching appraisals');
    }
  }, [dispatch, isEditMode, object_viewed_id]);

  // Extract section/category rows from appraisal progress (edit) or setups (new)
  useEffect(() => {
    const extractSectionsFromData = () => {
      // In edit mode, use data from the appraisal progress record
      if (isEditMode && appraisalState.appraisalProgressRecord) {
        const record = mergeAppraisalProgressRecord(
          appraisalState.appraisalProgressRecord,
          appraisalState.appraisalProgressList
        );
        const recordSectionBlob = record.sections_data ?? record[F.sectionDataSynonym];

        // Object shape: { "Group title": [{ title, rating, weightage, ... }, ...], ... }
        const categoriesMap = {};

        if (recordSectionBlob && typeof recordSectionBlob === 'object' && !Array.isArray(recordSectionBlob)) {
          Object.keys(recordSectionBlob).forEach((groupTitle) => {
            const categories = recordSectionBlob[groupTitle];
            
            if (Array.isArray(categories) && categories.length > 0) {
              if (!categoriesMap[groupTitle]) {
                categoriesMap[groupTitle] = [];
              }
              
              categories.forEach((categoryItem, index) => {
                categoriesMap[groupTitle].push({
                  id: categoryItem.id || `row-${groupTitle}-${index}`,
                  title: categoryItem.title || groupTitle,
                  rating: categoryItem.rating || null,
                  weightage: categoryItem.weightage || null,
                  description: categoryItem.description || "",
                  [F.setupPivotKey]: categoryItem.id || `row-${groupTitle}-${index}`,
                  [F.titleSynonym]: groupTitle,
                  obtained_score: categoryItem.obtained_score ?? null,
                  comments: categoryItem.comments ?? "",
                });
              });
            }
          });
        } else if (recordSectionBlob && Array.isArray(recordSectionBlob) && recordSectionBlob.length > 0) {
          recordSectionBlob.forEach((sectionRow, index) => {
            const groupTitle = sectionRow.title || 'Section';
            
            if (!categoriesMap[groupTitle]) {
              categoriesMap[groupTitle] = [];
            }
            
            categoriesMap[groupTitle].push({
              id: sectionRow.id || `row-${index}`,
              title: groupTitle,
              rating: sectionRow.rating || null,
              weightage: sectionRow.weightage || null,
              description: sectionRow.description || "",
              [F.setupPivotKey]: sectionRow.id || `row-${index}`,
              [F.titleSynonym]: groupTitle,
              obtained_score: sectionRow.obtained_score ?? null,
              comments: sectionRow.comments ?? "",
            });
          });
        } else if (record.appraisal_setups && Array.isArray(record.appraisal_setups)) {
          record.appraisal_setups.forEach((setup) => {
            if (setup[F.setupPivotRows] && Array.isArray(setup[F.setupPivotRows])) {
              setup[F.setupPivotRows].forEach((setupRow) => {
                const groupTitle = setupRow[F.pivotRelation]?.title || 'Section';
                
                if (!categoriesMap[groupTitle]) {
                  categoriesMap[groupTitle] = [];
                }
                
                const savedRow = formState.sectionData?.[setupRow.id] || {};
                
                categoriesMap[groupTitle].push({
                  id: setupRow.id,
                  title: groupTitle,
                  rating: savedRow.rating || setupRow.rating || null,
                  weightage: savedRow.weightage || setupRow.weightage || null,
                  description: savedRow.description || setupRow[F.pivotRelation]?.description || "",
                  [F.setupPivotKey]: setupRow.id,
                  [F.titleSynonym]: groupTitle,
                  obtained_score: savedRow.obtained_score ?? null,
                  comments: savedRow.comments ?? "",
                });
              });
            }
          });
        }
        
        const appraisalFormName = resolveAppraisalFormNameFromRecord(record);
        const appraiserName = resolveAppraiserNameFromRecord(
          record,
          employeeState.employeesList
        );
        if (appraisalFormName || appraiserName) {
          setFormState((prev) => ({
            ...prev,
            ...(appraisalFormName ? { appraisal_name: appraisalFormName } : {}),
            ...(appraiserName ? { appraiserName } : {}),
          }));
        }
        
        setSectionCategories(categoriesMap);
        return;
      }
      
      // For new records, use appraisal setups list
      if (!formState.appraisalTypes || formState.appraisalTypes.length === 0) {
        setSectionCategories({});
        setAppraisalSetups([]);
        return;
      }

      if (!appraisalState.appraisalsList || appraisalState.appraisalsList.length === 0) {
        setSectionCategories({});
        setAppraisalSetups([]);
        return;
      }

      console.log('Extracting sections from appraisal setups for IDs:', formState.appraisalTypes);

      try {
        // Get setups from appraisalsList
        const setups = formState.appraisalTypes
          .map(setupId => appraisalState.appraisalsList.find(app => app.id === setupId))
          .filter(Boolean);
        
        setAppraisalSetups(setups);
        console.log('Found setups from appraisalsList:', setups.length);

        const categoriesMap = {};
        
        if (setups.length === 0) {
          console.warn('No setups found to process');
          setSectionCategories({});
          return;
        }
        
        setups.forEach((setup) => {
          const setupSections = setup.sections ?? setup[F.listAlternatePlural];
          console.log('Processing setup:', setup?.id, 'sections:', setupSections?.length);
          if (setup && setupSections && Array.isArray(setupSections) && setupSections.length > 0) {
            setupSections.forEach((sectionRow) => {
              const groupTitle = sectionRow.title || 'Section';
              console.log('Processing section row:', groupTitle, sectionRow);
              
              if (!categoriesMap[groupTitle]) {
                categoriesMap[groupTitle] = [];
              }
              
              categoriesMap[groupTitle].push({
                id: sectionRow.pivot?.id || sectionRow.id,
                title: sectionRow.title || "",
                rating: null, // Not available in list endpoint
                weightage: null, // Not available in list endpoint
                description: sectionRow.description || "",
                [F.setupPivotKey]: sectionRow.pivot?.id || sectionRow.id,
                [F.titleSynonym]: sectionRow.title || "",
                obtained_score: null, // Populated from formState.sectionData when available
                comments: "",
              });
            });
          } else {
            console.log('Setup has no section list rows or pivot list is empty:', setup);
          }
        });

        console.log('Final categoriesMap:', categoriesMap, 'Keys:', Object.keys(categoriesMap));
        setSectionCategories(categoriesMap);
        
        // Set appraisal name from the first setup's title
        if (setups.length > 0 && setups[0].title) {
          setFormState(prev => ({
            ...prev,
            appraisal_name: setups[0].title
          }));
        }
      } catch (error) {
        console.error('Error extracting sections from appraisal setups:', error);
        setSectionCategories({});
      }
    };

    extractSectionsFromData();
  }, [
    isEditMode,
    formState.appraisalTypes,
    appraisalState.appraisalsList,
    appraisalState.appraisalProgressRecord,
    appraisalState.appraisalProgressList,
    employeeState.employeesList,
    formState.sectionData,
  ]);

  // Keep header fields in sync when list/employees load after detail API
  useEffect(() => {
    if (!isEditMode || !appraisalState.appraisalProgressRecord) return;

    const record = mergeAppraisalProgressRecord(
      appraisalState.appraisalProgressRecord,
      appraisalState.appraisalProgressList
    );
    const appraisalFormName = resolveAppraisalFormNameFromRecord(record);
    const appraiserName = resolveAppraiserNameFromRecord(
      record,
      employeeState.employeesList
    );

    if (!appraisalFormName && !appraiserName) return;

    setFormState((prev) => ({
      ...prev,
      ...(appraisalFormName && !prev.appraisal_name ? { appraisal_name: appraisalFormName } : {}),
      ...(appraiserName && (!prev.appraiserName || appraiserName !== prev.appraiserName)
        ? { appraiserName }
        : {}),
    }));
  }, [
    isEditMode,
    appraisalState.appraisalProgressRecord,
    appraisalState.appraisalProgressList,
    employeeState.employeesList,
  ]);

  // Convert employees list to dropdown options
  useEffect(() => {
    if (employeeState.employeesList && employeeState.employeesList.length > 0) {
      const options = map(employeeState.employeesList, (employee) => {
        // Construct full name from firstname, middlename, and lastname
        const nameParts = [
          employee.firstname,
          employee.middlename,
          employee.lastname
        ].filter(Boolean); // Remove null/undefined/empty values
        
        const employeeName = nameParts.length > 0 
          ? nameParts.join(' ').trim()
          : employee.name || employee.client_name || employee.employee_name || employee.full_name || `Employee ${employee.id}`;
        
        return {
          value: employee.id, // ID is stored as value but not displayed
          label: employeeName, // Only name is displayed in dropdown
        };
      });
      setEmployeeOptions(options);
    }
  }, [employeeState.employeesList]);

  // Convert appraisals list to dropdown options
  useEffect(() => {
    if (appraisalState.appraisalsList && appraisalState.appraisalsList.length > 0) {
      let options = map(appraisalState.appraisalsList, (appraisal) => ({
        value: appraisal.id,
        label: appraisal.title || `Appraisal ${appraisal.id}`,
      }));
      
      setAppraisalOptions(options);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appraisalState.appraisalsList]);
  
  // Set selected appraisal type based on appraisal_types field (for display)
  useEffect(() => {
    if (formState.appraisal_types && formState.appraisal_types.trim() !== "") {
      // Use appraisal_types as the displayed value (read-only)
      const typeOption = {
        value: formState.appraisal_types,
        label: formState.appraisal_types,
      };
      
      // Only update if different from current selection
      if (selectedAppraisals.length === 0 || 
          selectedAppraisals[0]?.value !== formState.appraisal_types ||
          selectedAppraisals[0]?.label !== formState.appraisal_types) {
        setSelectedAppraisals([typeOption]);
      }
    } else if (!isEditMode) {
      // For new records, clear selection
      setSelectedAppraisals([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.appraisal_types, isEditMode]);

  // Load data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      const recordId = routeRecordId || location.state?.recordId || appraisalState.appraisalProgressRecord?.id;
      if (recordId && !appraisalState.appraisalProgressRecord) {
        dispatch(getAppraisalProgressById(recordId, 'update'));
      } else if (appraisalState.appraisalProgressRecord && !formState.id) {
        // Record already loaded, populate form
        loadRecordData(appraisalState.appraisalProgressRecord);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, location.state?.recordId, routeRecordId, dispatch]);

  // Load record data when it's available and appraisal options are ready
  useEffect(() => {
    if (appraisalState.appraisalProgressRecord && isEditMode && !formState.id) {
      // In edit mode, we don't need employeeOptions since employee_name comes from the record
      // We only need appraisalOptions if we're using dropdowns, but for edit mode we can load without them
      if (appraisalOptions.length > 0 || isEditMode) {
        loadRecordData(appraisalState.appraisalProgressRecord);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appraisalState.appraisalProgressRecord, isEditMode, appraisalOptions]);

  // Ensure appraisalTypes is set from record if available
  useEffect(() => {
    if (appraisalState.appraisalProgressRecord && isEditMode && formState.appraisalTypes.length === 0) {
      const record = appraisalState.appraisalProgressRecord;
      const appraisalSetupIds = record.appraisal_setup_ids || 
                                (record.appraisal_setups ? record.appraisal_setups.map(a => a.id) : []) ||
                                [];
      if (appraisalSetupIds.length > 0) {
        setFormState(prev => ({ ...prev, appraisalTypes: appraisalSetupIds }));
      }
    }
  }, [appraisalState.appraisalProgressRecord, isEditMode, formState.appraisalTypes.length]);

  // Set selected employee when employeeOptions become available and we have an employeeId
  useEffect(() => {
    if (formState.employeeId && employeeOptions.length > 0 && !selectedEmployee) {
      const employee = find(employeeOptions, ['value', formState.employeeId]);
      if (employee) {
        setSelectedEmployee(employee);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeOptions, formState.employeeId]);

  const loadRecordData = (rawRecord) => {
    if (!rawRecord) return;

    const record = mergeAppraisalProgressRecord(
      rawRecord,
      appraisalState.appraisalProgressList
    );
    const employeesList = employeeState.employeesList || [];

    // Parse appraisal_date_time - check multiple possible formats
    let appraisalDateTime = null;
    const dateTimeValue = record.appraisal_date_time || record.appraisalDateTime;
    const formattedDate = record.appraisal_date_time_formatted;
    
    if (dateTimeValue) {
      try {
        // If it's already a Date object, use it
        if (dateTimeValue instanceof Date) {
          appraisalDateTime = dateTimeValue;
        } else {
          // Try parsing as ISO string or formatted string
          appraisalDateTime = new Date(dateTimeValue);
          if (isNaN(appraisalDateTime.getTime())) {
            appraisalDateTime = null;
          }
        }
      } catch {
        appraisalDateTime = null;
      }
    }
    
    // If we still don't have a valid date, try parsing formatted date
    if (!appraisalDateTime && formattedDate) {
      try {
        // Parse formatted date like "2026-03-11 20:48"
        // Convert to ISO format: "2026-03-11T20:48:00"
        const isoDate = formattedDate.replace(' ', 'T') + ':00';
        appraisalDateTime = new Date(isoDate);
        if (isNaN(appraisalDateTime.getTime())) {
          // Try alternative parsing without seconds
          const isoDateAlt = formattedDate.replace(' ', 'T');
          appraisalDateTime = new Date(isoDateAlt);
          if (isNaN(appraisalDateTime.getTime())) {
            appraisalDateTime = null;
          }
        }
      } catch {
        appraisalDateTime = null;
      }
    }

    // Parse sign_off_date_time if it exists
    let signOffDateTime = null;
    if (record.sign_off_date_time) {
      try {
        signOffDateTime = new Date(record.sign_off_date_time);
        if (isNaN(signOffDateTime.getTime())) {
          signOffDateTime = null;
        }
      } catch {
        signOffDateTime = null;
      }
    }

    // Get appraisal setup IDs
    const appraisalSetupIds = record.appraisal_setup_ids || 
                              (record.appraisal_setups ? record.appraisal_setups.map(a => a.id) : []) ||
                              [];
    
    // Always set appraisalTypes in formState so fetch can happen
    setFormState(prev => ({ ...prev, appraisalTypes: appraisalSetupIds }));
    
    // Set selected appraisals for dropdown
    if (appraisalSetupIds.length > 0 && appraisalOptions.length > 0) {
      const selected = appraisalOptions.filter(opt => appraisalSetupIds.includes(opt.value));
      setSelectedAppraisals(selected);
    }

    // Set selected employee if employee_id exists
    const employeeId = record.employee_id || record.employeeId;
    if (employeeId && employeeOptions.length > 0) {
      const employee = find(employeeOptions, ['value', employeeId]);
      if (employee) {
        setSelectedEmployee(employee);
      }
    } else if (record.employee_name || record.employeeName) {
      // Try to find employee by name if ID is not available
      const employeeName = record.employee_name || record.employeeName;
      const employee = find(employeeOptions, ['label', employeeName]);
      if (employee) {
        setSelectedEmployee(employee);
      }
    }

    // Get appraisal_types field (string like "General")
    const appraisalTypesString = record.appraisal_types || record.appraisalTypes || "";

    // Log for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log('Loading record data:', {
        hasAppraisalDateTime: !!appraisalDateTime,
        appraisalDateTime: appraisalDateTime,
        recordAppraisalDateTime: record.appraisal_date_time,
        recordAppraisalDateTimeFormatted: record.appraisal_date_time_formatted
      });
    }

    setFormState(prev => ({
      ...prev,
      id: record.id,
      employeeName:
        resolvePersonDisplayName(record.employee) ||
        record.employee_name ||
        record.employeeName ||
        "",
      employeeId: employeeId || null,
      appraisalDateTime: appraisalDateTime || prev.appraisalDateTime, // Preserve existing date if parsing fails
      appraisalTypes: appraisalSetupIds,
      appraisal_types: appraisalTypesString,
      appraisal_name: resolveAppraisalFormNameFromRecord(record),
      appraiserName: resolveAppraiserNameFromRecord(record, employeesList),
      appraisersComments: record.appraisers_comments || record.appraisersComments || "",
      objective1: record.objective1 || "",
      objective2: record.objective2 || "",
      objective3: record.objective3 || "",
      developmentalPlan: record.developmental_plan || record.developmentalPlan || "",
      signOff: record.sign_off || record.signOff || "",
      signOffDateTime: signOffDateTime,
      signatureImage: record.signature_image || record.signatureImage || "",
      employeeSignature: record.employee_signature || record.employeeSignature || "",
      employeeSignedAt: record.employee_signed_at || record.employeeSignedAt || null,
      show_rtm: record.show_rtm ?? record.showRTM ?? prev.show_rtm ?? 0,
      show_rtm_and_update_button:
        record.show_rtm_and_update_button ??
        record.showRTMandUpdateButton ??
        prev.show_rtm_and_update_button ??
        0,
        status: record.status || record.appraisal_status || prev.status || "",
        current_approval_level: record.current_approval_level ?? record.approval_level ?? prev.current_approval_level ?? null,
        approval_level: record.approval_level ?? prev.approval_level ?? null,
        created_by: record.created_by ?? record.createdBy ?? prev.created_by ?? null,
        current_user_role: record.current_user_role ?? record.currentUserRole ?? record.user_role ?? record.userRole ?? null,
        can_edit: record.can_edit ?? record.canEdit ?? prev.can_edit ?? null,
        can_approve: record.can_approve ?? record.canApprove ?? prev.can_approve ?? null,
        can_reject: record.can_reject ?? record.canReject ?? prev.can_reject ?? null,
        approval_levels: record.approval_levels ?? record.approvalLevels ?? prev.approval_levels ?? null,
        appraiser_id: record.appraiser_id ?? record.appraiserId ?? prev.appraiser_id ?? null,
        second_approver_id: record.second_approver_id ?? record.secondApproverId ?? prev.second_approver_id ?? null,
        appraiser: record.appraiser ?? prev.appraiser ?? null,
      sectionData: record.sections_data ?? record[F.sectionDataSynonym] ?? record[F.camelData] ?? {},
    }));
  };

  // Create category tabs from grouped categories
  useEffect(() => {
    const categoryTabs = Object.keys(sectionCategories).map((categoryTitle) => ({
      label: categoryTitle,
      value: `category-${categoryTitle}`,
      type: 'category',
    }));
    
    console.log('Creating category tabs:', categoryTabs.length, categoryTabs);
    
    // Store category tabs for rendering
    setSelectedSectionTabs(categoryTabs);
    
    // Set active key to first category tab if available and no active key is set
    if (categoryTabs.length > 0) {
      if (!activeKey || !categoryTabs.some(tab => tab.value === activeKey)) {
        setActiveKey(categoryTabs[0].value);
      }
    } else if (!activeKey) {
      // If no categories, set to first hardcoded tab
      setActiveKey("AppraisersComments​");
    }
  }, [sectionCategories]); // eslint-disable-line react-hooks/exhaustive-deps

  const hardcodedTabs = [
    { label: "Appraiser's Comments​", value: "AppraisersComments​", type: 'hardcoded' },
    { label: "Developmental Plan​", value: "DevelopmentalPlan​", type: 'hardcoded' },
    { label: "Appraiser's Sign", value: "SignOff​", type: 'hardcoded' }
  ];

  const allTabs = [...selectedSectionTabs, ...hardcodedTabs];
  const baseReadOnlyRoles = ["supervisor", "hr", "operations_manager"];
  const normalizedCurrentUserRole = String(formState?.current_user_role || "").toLowerCase();
  const canEditRaw = formState?.can_edit;
  const hasCanEditFlag = canEditRaw !== null && canEditRaw !== undefined;
  const canEditFromRecord = canEditRaw === 1 || canEditRaw === "1";
  const readOnlyRoles = hasCanEditFlag
    ? canEditFromRecord
      ? baseReadOnlyRoles.filter((role) => role !== normalizedCurrentUserRole)
      : Array.from(new Set([...baseReadOnlyRoles, normalizedCurrentUserRole].filter(Boolean)))
    : baseReadOnlyRoles;
  const isOneLevelWorkflow = Number(formState?.approval_levels) === 1;
  const isReadOnly = isOneLevelWorkflow
    ? hasCanEditFlag
      ? !canEditFromRecord
      : true
    : readOnlyRoles.includes(normalizedCurrentUserRole);
  const isHrReadOnly = isReadOnly && normalizedCurrentUserRole === "hr";

  const progressRecord = useMemo(
    () =>
      mergeAppraisalProgressRecord(
        appraisalState.appraisalProgressRecord,
        appraisalState.appraisalProgressList
      ),
    [appraisalState.appraisalProgressRecord, appraisalState.appraisalProgressList]
  );

  const managerRejectionRemarks = useMemo(
    () => getManagerRejectionRemarksFromRecord(progressRecord),
    [progressRecord]
  );

  const showManagerRejectionBar = useMemo(() => {
    const role = String(getUserRole(session) || normalizedCurrentUserRole || "")
      .trim()
      .toLowerCase();
    if (role !== "supervisor") return false;
    if (!isEditMode || !progressRecord?.id) return false;
    if (managerRejectionRemarks) return true;
    return isAppraisalReturnedToSupervisor(progressRecord);
  }, [session, normalizedCurrentUserRole, isEditMode, progressRecord, managerRejectionRemarks]);

  const sectionWeightageByTitle = useMemo(() => {
    const weights = {};
    const record = appraisalState.appraisalProgressRecord;

    (record?.sections ?? record?.[F.listAlternatePlural] ?? []).forEach((sectionRow) => {
      const titleKey = String(sectionRow?.title ?? "").trim().toLowerCase();
      const weight = Number(sectionRow?.weightage);
      if (titleKey && Number.isFinite(weight) && weight > 0) {
        weights[titleKey] = weight;
      }
    });

    appraisalSetups.forEach((setup) => {
      getAppraisalSetupSectionPivotRows(setup).forEach((pivotRow) => {
        const titleKey = String(
          pivotRow[F.pivotRelation]?.title || pivotRow.title || ""
        )
          .trim()
          .toLowerCase();
        const weight = Number(pivotRow?.weightage ?? pivotRow?.pivot?.weightage);
        if (titleKey && Number.isFinite(weight) && weight > 0) {
          weights[titleKey] = weight;
        }
      });
    });

    return weights;
  }, [
    appraisalState.appraisalProgressRecord,
    appraisalSetups,
  ]);

  const liveSummaryPercentage = useMemo(
    () =>
      calculateAppraisalSummaryPercentage({
        sectionCategories,
        sectionData: formState.sectionData,
        sectionWeightageByTitle,
      }),
    [sectionCategories, formState.sectionData, sectionWeightageByTitle]
  );

  const liveSummaryDisplay = formatSummaryPercentageDisplay(liveSummaryPercentage);

  const validateSectionScores = () => {
    const validationErrors = [];

    Object.keys(sectionCategories || {}).forEach((categoryTitle) => {
      const categoryRows = sectionCategories[categoryTitle] || [];

      categoryRows.forEach((row) => {
        const groupTitle = row[F.titleSynonym] || categoryTitle;
        let savedData = null;

        if (formState.sectionData && formState.sectionData[groupTitle] && Array.isArray(formState.sectionData[groupTitle])) {
          savedData = formState.sectionData[groupTitle].find((cat) => cat.title === row.title);
        }

        if (!savedData) {
          savedData = formState.sectionData?.[row[F.setupPivotKey]] || formState.sectionData?.[row.id];
        }

        const totalScore = Number(savedData?.rating ?? row.rating);
        const obtainedScore = Number(savedData?.obtained_score ?? row.obtained_score);

        if (!isValidMaxRating(totalScore)) {
          validationErrors.push(`${row.title || "Question"}: Max Rating must be percentage (0-100%).`);
          return;
        }

        if (!Number.isFinite(obtainedScore) || obtainedScore <= 0) {
          validationErrors.push(`${row.title || "Question"}: Obtained Rating must be a positive numeric value.`);
        }
      });
    });

    return validationErrors;
  };

  const handleSave = async ({ redirect = true, skipSectionValidation = false, fields = {} } = {}) => {
    try {
      const currentPermissions = session?.current_page_permissions;
      
      if (currentPermissions && !Array.isArray(currentPermissions) && Object.keys(currentPermissions).length > 0) {
        const hasPermission = isEditMode 
          ? (currentPermissions.rights_edit === 1 || currentPermissions.rights_edit === '1')
          : (currentPermissions.rights_add === 1 || currentPermissions.rights_add === '1');

        if (!hasPermission) {
          alert(`You do not have permission to ${isEditMode ? 'edit' : 'create'} appraisal progress records. Please contact your administrator.`);
          return { success: false, id: null };
        }
      }

      const saveObjectViewedId =
        fields.objectViewedId ||
        location.state?.objectViewedId ||
        object_viewed_id ||
        routeObjectViewedId ||
        (currentPermissions && !Array.isArray(currentPermissions) ? currentPermissions.object_id : "") ||
        session?.current_object_viewed_id ||
        "";

      if (!saveObjectViewedId) {
        alert('Unable to determine object context. Please refresh the page and try again.');
        return { success: false, id: null };
      }

      // Format appraisal_date_time if it's a Date object
      let formattedDateTime = formState.appraisalDateTime;
      if (formattedDateTime instanceof Date) {
        formattedDateTime = formattedDateTime.toISOString();
      } else if (formattedDateTime === null || formattedDateTime === undefined) {
        // Validation: appraisal_date_time is required
        alert('The appraisal date and time is required.');
        return;
      } else if (typeof formattedDateTime === 'string') {
        // If it's already a string, validate it's a valid date format
        const testDate = new Date(formattedDateTime);
        if (isNaN(testDate.getTime())) {
          alert('The appraisal date and time is invalid.');
          return;
        }
        // Use the string as-is if it's valid
      }

      // sign_off_date_time should be set by backend automatically when sign_off becomes true.
      // Frontend will not generate a sign-off timestamp.
      const formattedSignOffDateTime = null;

      if (!skipSectionValidation) {
        const sectionScoreErrors = validateSectionScores();
        if (sectionScoreErrors.length > 0) {
          alert(sectionScoreErrors[0]);
          return;
        }
      }

      // Build formData with all required database fields mapped correctly
      const signOffValue = fields.signOff ?? formState.signOff ?? false;
      const signatureValue = fields.signatureImage ?? formState.signatureImage ?? null;

      const formData = {
        id: formState.id || null,
        employee_id: formState.employeeId,
        appraisal_date_time: formattedDateTime,
        appraisal_types: formState.appraisal_types || null,
        appraisal_setup_ids: formState.appraisalTypes || [],
        appraisers_comments: formState.appraisersComments || null,
        developmental_plan: formState.developmentalPlan || null,
        [F.sectionDataSynonym]:
          formState.sectionData && Object.keys(formState.sectionData).length > 0 ? formState.sectionData : null,
        sections_data: formState.sectionData && Object.keys(formState.sectionData).length > 0 ? formState.sectionData : null,
        objective1: formState.objective1 || null,
        objective2: formState.objective2 || null,
        objective3: formState.objective3 || null,
        sign_off: signOffValue || false,
        sign_off_date_time: formattedSignOffDateTime,
        signature_image: signatureValue,
        object_viewed_id: saveObjectViewedId,
      };

      const method = isEditMode ? "PUT" : "POST";
      const saveResponse = await dispatch(createOrUpdateAppraisalProgress(formData, method));
      const savedRecord = saveResponse?.data || saveResponse;
      const savedRecordId =
        formState.id ||
        savedRecord?.id ||
        saveResponse?.id ||
        null;
      const savedStatus = savedRecord?.status || null;

      if (savedRecordId && !formState.id) {
        setFormState((prev) => ({ ...prev, id: savedRecordId }));
      }
      if (fields.signOff !== undefined || fields.signatureImage !== undefined) {
        setFormState((prev) => ({
          ...prev,
          signOff: fields.signOff ?? prev.signOff,
          signatureImage: fields.signatureImage ?? prev.signatureImage,
        }));
      }
      
      // Redirect to list after successful save.
      // Only auto-navigate on a *new* transition into the manager RTM queue (e.g. supervisor Sign Off),
      // not when the PAF was already awaiting manager review (manager Reject must open the modal first).
      const rtmStatuses = ["sent_to_rtm", "sent_rtm", "auto_sent_rtm"];
      const priorStatus = String(formState.status || "").toLowerCase();
      const nextStatus = String(savedStatus || "").toLowerCase();
      const wasAlreadyRtm = rtmStatuses.includes(priorStatus);
      const becameRtm = !wasAlreadyRtm && rtmStatuses.includes(nextStatus);
      if (redirect || (fields.signOff && becameRtm)) {
        navigate(listPath);
      }

      return { success: true, id: savedRecordId, status: savedStatus };
    } catch (error) {
      console.error('Error saving appraisal progress:', error);
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'An error occurred while saving. Please try again.';
      alert(errorMessage);
      return { success: false, id: null, error };
    }
  };

  const goToNextTab = async (currentTabKey) => {
    const idx = allTabs.findIndex((t) => t.value === currentTabKey);
    if (idx < 0 || idx >= allTabs.length - 1) return;
    if (!isReadOnly) {
      const saveResult = await handleSave({ redirect: false, skipSectionValidation: true });
      if (!saveResult || saveResult.success !== true) return;
    }
    setActiveKey(allTabs[idx + 1].value);
  };

  const handleCancel = () => {
    navigate(listPath);
  };

  // Get permissions (e.g. Sign-Off approve / reject)
  const userRole = getUserRole(session);
  const currentUserId = getCurrentUserId(session);
  const permissions = getAppraisalPermissions({
    role: userRole,
    record: formState,
    currentUserId: currentUserId,
    currentEmployeeId: getCurrentEmployeeId(session),
  });

  const handleApprovalSuccess = () => {
    setApprovalModal({ show: false, actionType: null });
    // After approval/rejection, navigate back to list
    navigate(listPath);
  };

  const handleDirectApprove = async () => {
    if (!formState.id) return;
    try {
      await dispatch(
        operationsManagerApprove(
          formState.id,
          formState.appraisersComments || "",
          object_viewed_id
        )
      );
      navigate(listPath);
    } catch (error) {
      console.error("Error approving appraisal:", error);
    }
  };

  const updateFormField = (field, value) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  };

  const updateSectionData = (questionKey, updated) => {
    setFormState(prev => {
      const groupTitle = updated[F.titleSynonym];
      const categoryTitle = updated.title;
      const categoryId = updated.id || updated[F.setupPivotKey] || null;
      
      if (!groupTitle) {
        return {
          ...prev,
          sectionData: {
            ...prev.sectionData,
            [questionKey]: updated
          }
        };
      }
      
      const merged = { ...prev.sectionData };
      
      if (!merged[groupTitle]) {
        merged[groupTitle] = [];
      }
      
      // Find and update the specific category using stable identity first, then fallback to title
      const categoryIndex = merged[groupTitle].findIndex(
        cat => {
          const existingId = cat.id || cat[F.setupPivotKey] || null;
          if (categoryId && existingId) {
            return existingId === categoryId;
          }
          return cat.title === categoryTitle;
        }
      );
      
      if (categoryIndex >= 0) {
        merged[groupTitle][categoryIndex] = {
          ...merged[groupTitle][categoryIndex],
          obtained_score: updated.obtained_score,
          rating: updated.rating,
          weightage: updated.weightage,
          description: updated.description || merged[groupTitle][categoryIndex].description || "",
          comments: updated.comments ?? merged[groupTitle][categoryIndex].comments ?? "",
          id: categoryId || merged[groupTitle][categoryIndex].id || null,
          [F.setupPivotKey]: updated[F.setupPivotKey] || merged[groupTitle][categoryIndex][F.setupPivotKey] || null,
        };
      } else {
        merged[groupTitle].push({
          id: categoryId,
          [F.setupPivotKey]: updated[F.setupPivotKey] || null,
          title: categoryTitle,
          rating: updated.rating,
          weightage: updated.weightage,
          obtained_score: updated.obtained_score,
          description: updated.description || "",
          comments: updated.comments ?? "",
        });
      }
      
      return {
        ...prev,
        sectionData: merged
      };
    });
  };

  const renderTabContent = (tabKey) => {
    const tabIndex = allTabs.findIndex((t) => t.value === tabKey);
    const hasNextTab = tabIndex >= 0 && tabIndex < allTabs.length - 1;
    const nextTabControl =
      !isHrReadOnly && hasNextTab
        ? {
            onNext: () => goToNextTab(tabKey),
            hasNextTab,
          }
        : { onNext: undefined, hasNextTab: false };

    // Handle category tabs
    if (tabKey && tabKey.startsWith('category-')) {
      const categoryTitle = tabKey.replace('category-', '');
      const categoryRows = sectionCategories[categoryTitle] || [];
      
      const rowsWithMergedState = categoryRows.map((row) => {
        const merged = mergeScoringRowWithSectionData(
          row,
          categoryTitle,
          formState.sectionData
        );
        return {
          ...merged,
          description: merged.description ?? row.description,
          comments: merged.comments ?? row.comments ?? "",
        };
      });
      
      return (
        <SectionCategoryTab
          categoryName={categoryTitle}
          sections={rowsWithMergedState}
          onNext={nextTabControl.onNext}
          hasNextTab={nextTabControl.hasNextTab}
          onCancel={handleCancel}
          onSectionChange={updateSectionData}
          isReadOnly={isReadOnly}
        />
      );
    }
    
    // Handle hardcoded tabs
    if (tabKey === "AppraisersComments​") {
      return (
        <AppraisalCommentTab
          formState={formState}
          updateFormField={updateFormField}
          onNext={nextTabControl.onNext}
          hasNextTab={nextTabControl.hasNextTab}
          onCancel={handleCancel}
          isReadOnly={isReadOnly}
          appraisalProgressId={formState.id}
          initialAttachments={progressRecord?.attachments}
        />
      );
    }
    if (tabKey === "DevelopmentalPlan​") {
      return (
        <DevelopmentPlanTab
          formState={formState}
          updateFormField={updateFormField}
          onNext={nextTabControl.onNext}
          hasNextTab={nextTabControl.hasNextTab}
          onCancel={handleCancel}
          isReadOnly={isReadOnly}
        />
      );
    }
    if (tabKey === "SignOff​") {
      return (
        <SignOffTab
          formState={formState}
          updateFormField={updateFormField}
          onSave={isHrReadOnly ? undefined : handleSave}
          onCancel={handleCancel}
          currentUserRole={normalizedCurrentUserRole}
          canApprove={permissions.canApprove}
          canReject={permissions.canReject}
          onApprove={handleDirectApprove}
          onReject={() => {
            // Do not save/navigate here — that previously redirected away before the
            // rejection modal could collect a required reason (status stayed sent_to_rtm).
            if (!formState.id) return;
            setApprovalModal({ show: true, actionType: "reject" });
          }}
          isReadOnly={isReadOnly}
          onRtmApprove={async () => {
            if (!formState.id) return;
            try {
              await dispatch(operationsManagerApprove(formState.id, "", object_viewed_id));
              navigate(listPath);
            } catch (error) {
              console.error("Error approving RTM:", error);
            }
          }}
          onRtmDisapprove={() => {
            // Same reject modal as onReject — never submit an empty reason.
            if (!formState.id) return;
            setApprovalModal({ show: true, actionType: "reject" });
          }}
          onSendForRTM={async () => {
            const saveResult = await handleSave({
              redirect: false,
              fields: {
                signOff: 1,
                signatureImage: formState.signatureImage,
              },
            });
            if (!saveResult?.success) {
              return;
            }
            const appraisalId = saveResult.id || formState.id;
            if (!appraisalId) {
              alert('Unable to determine appraisal progress ID. Please try again.');
              return;
            }

            const savedStatus = String(saveResult.status || "").toLowerCase();
            const alreadySentToRtm =
              savedStatus === "sent_to_rtm" ||
              savedStatus === "sent_rtm" ||
              savedStatus === "auto_sent_rtm";

            if (!alreadySentToRtm) {
              try {
                await dispatch(sendAppraisalProgressForRTM(appraisalId, object_viewed_id));
              } catch (error) {
                console.error('Error sending for RTM:', error);
                return;
              }
            }

            navigate(listPath);
          }}
        />
      );
    }
    
    return (
      <TabsContent />
    );
  };

  return (
    <>
      <AppraisalProgressFormHeader />
      <div className="appraisal-progress-form">
        <div className="p-3">
          <Row>
            <Col lg={3}>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Appraisal Form Name</Form.Label>
                <input
                  type="text"
                  className="form-control input-field w-100"
                  value={formState.appraisal_name || ""}
                  readOnly
                  disabled
                />
              </Form.Group>
            </Col>
            <Col lg={3}>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Appraiser Name</Form.Label>
                <input
                  type="text"
                  className="form-control input-field w-100"
                  value={formState.appraiserName || ""}
                  readOnly
                  disabled
                />
              </Form.Group>
            </Col>
            <Col lg={3}>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Employee Name</Form.Label>
                <input
                  type="text"
                  className="form-control input-field w-100"
                  value={formState.employeeName || ""}
                  readOnly
                  disabled
                />
              </Form.Group>
            </Col>
            <Col lg={3}>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Date</Form.Label>
                <DatePicker
                  selected={formState.appraisalDateTime}
                  onChange={(date) => setFormState(prev => ({ ...prev, appraisalDateTime: date }))}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="yyyy-MM-dd HH:mm"
                  timeCaption="Time"
                  className='form-control input-field w-100'
                  placeholderText="Date"
                  disabled={true}
                  isClearable={false}
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
        {showManagerRejectionBar && (
          <div className="px-3 pb-2">
            <div
              className="alert alert-danger mb-2 py-2"
              role="alert"
              style={{ backgroundColor: "#f8d7da", borderColor: "#f5c2c7", color: "#842029" }}
            >
              <span className="d-block font-weight-s-bold mb-1">Manager rejection comments</span>
              <span className="font-13">
                {managerRejectionRemarks ||
                  "This PAF was returned to you for revision. No comments were provided."}
              </span>
            </div>
          </div>
        )}
        {isReadOnly && (
          <div className="px-3 pb-2">
            <div className="alert alert-info mb-2 py-2">
              Read-only: fields are locked for role {formState?.current_user_role || "current role"}.
            </div>
          </div>
        )}

        <div className="appraisal-progress-form-tabs">
          {selectedSectionTabs.length > 0 && (
            <div className="appraisal-progress-summary" aria-live="polite">
              Summary:{" "}
              <strong>
                {liveSummaryDisplay != null ? `${liveSummaryDisplay}%` : "—"}
              </strong>
            </div>
          )}
          {allTabs.length > 0 ? (
            <Tabs activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
              {allTabs.map((tab) => (
                <Tab eventKey={tab.value} title={tab.label} key={tab.value}>
                  {renderTabContent(tab.value)}
                </Tab>
              ))}
            </Tabs>
          ) : (
            <p>No section data available. Please complete setup first.</p>
          )}
        </div>
      </div>
      <ApprovalModal
        show={approvalModal.show}
        onHide={() => setApprovalModal({ show: false, actionType: null })}
        appraisalId={formState.id}
        actionType={approvalModal.actionType}
        onSuccess={handleApprovalSuccess}
        objectViewedId={object_viewed_id}
      />
    </>
  );
};

export default AppraisalProgressForm;
